import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useLayoutEffect } from 'react';
import {
  Activity,
  BookOpen, FileText, LogOut, Rss, Plug, ShieldCheck,
  Library, GitCompare, Sparkles, Bot, Globe, Plus, Scale, MessagesSquare, FolderLock, Filter,
} from 'lucide-react';
import { NAV_UI_FEATURES, UI_FEATURE_GROUPS, type UiFeature } from '@repo/shared-types';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { useProjectTree } from '../../lib/project-tree';
import { useResizablePanel } from '../../lib/resizable';
import { ProjectNavTree } from './ProjectNavTree';
import { AddToKbRail } from './AddToKbRail';
import { ResizeHandle } from '../ui/resize-handle';
import { ThemeToggle } from '../ui/theme-toggle';

/**
 * Icon per navigable section. The sections themselves — their labels, routes, grouping and
 * order — come from UI_FEATURES in @repo/shared-types, which the API resolves flags against;
 * only the iconography is a UI concern, so only that lives here.
 *
 * The groups are meaningful, not decorative: STANDALONE sits first with no heading and holds
 * everything that is a view onto memory, and SETTINGS is configuration rather than such a view,
 * which is why it sits last and is the only one that still earns a heading.
 */
const FEATURE_ICONS: Record<UiFeature, typeof FileText> = {
  KB: Library,
  KB_ASK: Sparkles,
  KB_REVIEW: GitCompare,
  KB_QUALITY: Scale,
  ACTIVITY: Activity,
  AUTOMATIONS: Bot,
  FEED: Rss,
  CHATS: MessagesSquare,
  SPACES: FolderLock,
  INTEGRATIONS: Plug,
  GLOBAL_INTEGRATIONS: Globe,
  INGEST_RULES: Filter,
  ACCESS: ShieldCheck,
};

function userInitials(user: { name: string | null; email: string }) {
  if (user.name) {
    const parts = user.name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return user.email.slice(0, 2).toUpperCase();
}

interface NavItem {
  to: string;
  icon: typeof FileText;
  label: string;
}

function NavGroup({
  title, items, className, heading = true,
}: { title: string; items: NavItem[]; className?: string; heading?: boolean }) {
  // A group whose every section is switched off renders nothing at all — a bare heading over
  // an empty list reads like a loading failure.
  if (items.length === 0) return null;

  return (
    <div className={className}>
      {/* STANDALONE renders bare: its items are not a section *of* anything — see UiFeatureGroup. */}
      {heading && (
        <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">{title}</p>
      )}
      <div className="space-y-0.5">
        {items.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400'
                    : 'text-ink-muted hover:bg-surface-subtle hover:text-ink',
                )
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
      </div>
    </div>
  );
}

export function Sidebar() {
  const { user, logout, hasFeature, isSystemAdmin } = useAuth();
  const navigate = useNavigate();
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { organization } = useProjectTree();
  /**
   * The project the per-project sections point at.
   *
   * Org-level pages (`/automations`, `/add`) have no `:projectSlug`, and without a fallback every
   * project-scoped item below would build `/p//ingest` — a dead link on exactly the pages you are
   * most likely to navigate away from. The organization is the one project guaranteed to exist.
   */
  const slug = projectSlug ?? organization?.slug ?? '';

  /**
   * The sidebar hosts the whole project hierarchy — clients, projects and sub-projects — so it
   * has to be widenable: deep branches and long names are unreadable at nav width.
   */
  const panel = useResizablePanel({
    initial: 224, min: 200, max: 480, side: 'left', storageKey: 'sidebar.width',
  });

  // Published as a CSS variable because the sidebar is `fixed`: every page offsets its own
  // content with `pl-[var(--sidebar-w)]`, and threading a number through all of them (via a
  // context each page would have to read) buys nothing over one variable on the root.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', `${panel.width}px`);
  }, [panel.width]);

  const itemsFor = (group: (typeof UI_FEATURE_GROUPS)[number]['group']): NavItem[] =>
    NAV_UI_FEATURES.filter((definition) => definition.group === group && hasFeature(definition.feature)).map(
      (definition) => ({
        // An org-level section addresses the whole organization, so it has no project to sit
        // under and links to a bare path. Without this it would build `/p/<slug>/automations`,
        // a route that no longer exists.
        to: definition.orgLevel ? `/${definition.path}` : `/p/${slug}/${definition.path}`,
        icon: FEATURE_ICONS[definition.feature],
        label: definition.label,
      }),
    );

  const visibleGroups = UI_FEATURE_GROUPS.map((group) => ({ ...group, items: itemsFor(group.group) })).filter(
    (group) => group.items.length > 0,
  );

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <>
    {/* The right-edge rail rides with the sidebar rather than being mounted per page: it belongs
        to the same app chrome. Both are mounted once, by AppShell above the routes — the login and
        admin shells sit outside that shell and correctly get neither. */}
    <AddToKbRail />
    <aside
      className="fixed left-0 top-0 h-full bg-card border-r border-border flex flex-col z-40"
      style={{ width: panel.width }}
    >
      {/* Logo */}
      <div className="px-5 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">C</span>
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-ink leading-tight truncate">CDR Memory</div>
            <div className="text-[10px] text-ink-faint uppercase tracking-wide truncate">Review Console</div>
          </div>
        </div>
      </div>

      {/* The one action that belongs to no project, above the hierarchy it is not part of.
          
          Outside the scroll region deliberately: it is the primary way content enters the system,
          and a tall project tree must not be able to scroll it out of reach.
          
          Styled as an ordinary nav row — same paddings, same icon size, same active and hover
          treatment as every entry in `NavGroup`. It had a dashed border and a filled chip to mark it
          as an action rather than a destination, and in a rail of plain rows that read as an alert
          rather than as emphasis. The right-edge rail is where this action is given weight; here it
          only needs to be findable.
          
          Gated on KB for the same reason every writing surface is: a reader who cannot see the
          knowledge base has nowhere for this to land. */}
      {hasFeature('KB') && (
        <div className="px-3 py-3 border-b border-border flex-shrink-0">
          <NavLink
            to="/add"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400'
                  : 'text-ink-muted hover:bg-surface-subtle hover:text-ink',
              )
            }
          >
            <Plus size={16} />
            {/* Truncates rather than wraps: the sidebar is resizable down to 200px, and a two-line
                row would misalign with every row beneath it. */}
            <span className="truncate">Drop context</span>
          </NavLink>
        </div>
      )}

      {/* One scroll region for the hierarchy *and* the sections beneath it.
          
          `min-h-0` is load-bearing: a flex item defaults to `min-height: auto`, which refuses to
          shrink below its content, so without it a tall tree grows the column instead of
          scrolling and pushes the sections and footer off-screen with no way to reach them.
          
          One region rather than two, because two independently-scrolling panes in a rail this
          narrow means whichever one you are not in is stranded. */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* The project hierarchy — the sidebar's primary navigation, and its only primitive. With
            sub-projects, "which project am I in" is a question about position in a tree, and a list
            of names cannot answer it. Knowledge-base pages are deliberately *not* nested here: a
            client/project hierarchy and the structure of one project's documents are two different
            things, and one list holding both made the nav ambiguous. The pages are a view inside the
            project (KbTreePanel), reached from its overview or its row menu. */}
        <div className="py-3 border-b border-border">
          <ProjectNavTree activeSlug={slug || null} />
        </div>

        {/* Sections of the *current* project. The hierarchy above is how you choose a project; this
            is what you can do once you are in one.

            No Overview link here: selecting a project in the tree already navigates to its
            overview, so a nav entry would duplicate every row in the hierarchy above. */}
        <nav className="px-3 py-4">
          {visibleGroups.length === 0 ? (
            <p className="px-3 py-2 text-[11px] text-ink-faint leading-relaxed">
              No sections are enabled for your account. Ask an administrator to restore access.
            </p>
          ) : (
            visibleGroups.map((group, index) => (
              <NavGroup
                key={group.key}
                title={group.label}
                heading={group.heading !== false}
                items={group.items}
                className={index > 0 ? 'mt-5' : undefined}
              />
            ))
          )}
        </nav>
      </div>

      {/* Theme + user + logout. Outside the scroll region so signing out and switching theme stay
          reachable however deep the hierarchy grows. */}
      <div className="px-3 py-4 border-t border-border space-y-2 flex-shrink-0">
        {/* Not a UI_FEATURES section and deliberately not gateable: it explains the console rather
            than being part of it, and a user who has had sections taken away is precisely the one
            who needs it. Down here with Administration for the same reason — it is chrome, not a
            view onto memory. */}
        <NavLink
          to="/doc"
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-ink-muted hover:bg-surface-subtle hover:text-ink transition-colors"
        >
          <BookOpen size={16} />
          Documentation
        </NavLink>
        {isSystemAdmin && (
          <NavLink
            to="/admin"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-ink-muted hover:bg-surface-subtle hover:text-ink transition-colors"
          >
            <ShieldCheck size={16} />
            Administration
          </NavLink>
        )}
        <div className="px-2">
          <ThemeToggle />
        </div>
        {user && (
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-surface-subtle group">
            <div className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-950/50 flex items-center justify-center flex-shrink-0">
              <span className="text-orange-600 dark:text-orange-400 text-[10px] font-bold">{userInitials(user)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-ink-secondary truncate">{user.name ?? user.email}</p>
              {user.name && (
                <p className="text-[10px] text-ink-faint truncate">{user.email}</p>
              )}
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="opacity-0 group-hover:opacity-100 transition-opacity text-ink-faint hover:text-red-500 dark:hover:text-red-400"
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Straddles the right border rather than sitting inside it, so the grabbable strip is the
          edge the user is already aiming at. */}
      <ResizeHandle
        handleProps={panel.handleProps}
        onDoubleClick={panel.reset}
        className="absolute inset-y-0 right-0 translate-x-1/2 z-50"
      />
    </aside>
    </>
  );
}
